/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AccountDetails.scss';

import { array, bool, number, object, string } from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';

import AccountDetailsHeader from 'components/layouts/AccountDetails/AccountDetailsHeader';
import AccountDetailsTable from 'components/layouts/AccountDetails/AccountDetailsTable';
import AccountDetailsTransactions from 'components/layouts/AccountDetails/AccountDetailsTransactions';
import AccountDetailsCostBasisTable from 'components/layouts/AccountDetails/AccountDetailsCostBasisTable';
import ActivitySummaryChart from 'components/layouts/Activity/ActivitySummaryChart';
import PortfolioGrowthChart from 'components/layouts/Growth/PortfolioGrowthChart';
import TransactionPendingTable from 'components/layouts/Transaction/TransactionPendingTable';
import MobileAccountsCostBasisTable from 'components/layouts/AccountDetails/MobileAccountsCostBasisTable';
import MobileTransactionPendingTable from 'components/layouts/Transaction/MobileTransactionPendingTable';
import TransactionHistoryTable from 'components/layouts/Transaction/TransactionHistoryTable';
import MobileTransactionHistoryTable from 'components/layouts/Transaction/MobileTransactionHistoryTable';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import Content from 'services/Content';
import {
  getAccountHistory,
  getConversionHistory,
  getIndividualAccountGrowth,
  getTaxes
} from 'services/Rest';
import { PageHeader } from 'components/pages/Header/PageHeader';
import {
  getAccountForDetails,
  getAccountOwners,
  getAccountTransactionUrls,
  getActivitySummaryForAccount,
  getCurrentValueAsOfDate,
  getCurrentValueForAccount,
  getNumberOfAccountsForSelection,
  getPerformanceForAccount,
  getPerformanceDatesForAccount,
  getSharesAsOfDate,
  getSharesForAccount,
  getAccountDetailsId,
  getPriorBusinessDay, getBrokerageAccountsFromSweepMap
} from 'state/account/AccountSelectors';
import Store from 'state/Store';
import {
  selectHistoricalTransactionTimePeriod,
  selectHistoricalTransactionIncludedAccounts
} from 'state/transaction/TransactionActions';
import {
  getAccountHistoryTransactions,
  getAccountHistoryTransactionsGroups,
  getAccountPendingTransactions,
  getAccountPendingTransactionsGroups
} from 'state/transaction/TransactionSelectors';
import { getAccountsCostBasisTableData } from 'state/taxes/TaxSelectors';
import { getView, isDesktopLayout } from 'state/view/ViewSelectors';
import { MUTUAL_FUND, isCollegeSavings, isMutualFund } from 'services/Products';
import { SectionHeaderWithSeparator } from 'components/pages/Header/SectionHeaderWithSeparator';
import {
  ACTIVITY_SUMMARY_FILTER_TYPE,
  PORTFOLIO_GROWTH_FILTER_TYPE
} from 'state/performance/PerformancePageSelectors';
import PerformancePeriodFilter from '../Performance/PerformancePeriodFilter';
import ProductTypePerformanceChart from '../Performance/Chart/ProductTypePerformanceChart';
import {
  getActivitySummaryFilterIsNull,
  getFormattedDateRangeForActivitySummary
} from 'state/performance/ActivitySummaryFilterSelectors';
import {
  getFormattedDateRangeForPortfolioGrowth,
  getPortfolioGrowthFilterIsNull
} from 'state/performance/PortfolioGrowthFilterSelectors';
import { shouldRequestTaxes } from 'state/requests/RequestStatusSelectors';
import {
  resetFiltersForAccountDetails,
  resetFiltersForPerformance
} from 'state/performance/PerformancePageActions';
import { withOtelContext } from '@trp-ta-nitro/otel-react';
import otapi from '@opentelemetry/api';
import { isRMDEligible } from 'state/customer/CustomerSelectors';
import { getPortfolioAsOfDate } from 'state/portfolio/PortfolioSelectors';
import { isValidPerformanceConstraint } from 'state/performance/PerformancePageUtils';
import {
  addActivitySummaryTableErrorScreenEnabled,
  addPortfolioGrowthTableErrorScreenEnabled,
  isMoneyMarketCutoffEnabled,
  shareClassConversionEnabled,
  shareClassConversionTableEnabled
} from 'services/FeatureFlags';
import ShareClassConversionTable from './ShareClassConversionTable/ShareClassConversionTable';
import { yyyymmdd } from 'services/Dates';
import { getConversionHistoryResponse } from 'state/conversionHistory/ConversionHistorySelectors';
import { getAccountDescriptor } from 'services/Accounts';
import AccountDetailsWarningMessage from 'components/layouts/AccountDetails/AccountDetailsWarningMessage';
import { isNil } from 'lodash';
import ToolTip from 'components/shared/ToolTip/ToolTip';
import infoHelpIcon from 'assets/icons/info-help-icon-filled.svg';

const AccountDetails = ({
  account,
  accountActivitySummary,
  accountPerformance,
  accountPerformanceDates,
  accountOwners,
  asOfDate,
  costBasisTableData,
  currentValue,
  currentValueAsOfDate,
  history,
  historyTransactions,
  historyTransactionsGroups,
  individualGrowth,
  isDesktopLayout,
  numberOfAccounts,
  otelContext,
  pendingTransactions,
  pendingTransactionsGroups,
  performancePeriods,
  shares,
  sharesAsOfDate,
  transactionUrls,
  view,
  accountDetailsId,
  location,
  activitySummaryDateRange,
  portfolioGrowthDateRange,
  shouldRequestTaxes,
  portfolioAsOfDate,
  conversionHistory,
  priorBusinessDay,
  isRMDEligible,
  isActivitySummaryNull,
  isPortfolioGrowthNull,
  sweepAccountsMap,
}) => {
  const showNewErrorScreen =
    addActivitySummaryTableErrorScreenEnabled() && isActivitySummaryNull;
  useEffect(() => {
    // on load
    Store.dispatch(resetFiltersForAccountDetails());

    // on unmount
    return () => {
      Store.dispatch(resetFiltersForPerformance());
    };
  }, []);

  useEffect(() => {
    if (shouldRequestTaxes) {
      otapi.context.with(otelContext, () => {
        getTaxes();
      });
    }
  }, [shouldRequestTaxes, otelContext]);

  useEffect(() => {
    if (accountDetailsId === null) {
      history.push('/accounts');
    }
  }, [accountDetailsId, history]);

  useEffect(() => {
    otapi.context.with(otelContext, () => {
      getAccountHistory(account, activitySummaryDateRange);
    });
  }, [account, activitySummaryDateRange, otelContext]);

  useEffect(() => {
    otapi.context.with(otelContext, () => {
      if (
        isValidPerformanceConstraint(
          [account],
          portfolioGrowthDateRange.fromDate,
          portfolioGrowthDateRange.toDate
        )
      ) {
        getIndividualAccountGrowth(account, portfolioGrowthDateRange);
      }
    });
  }, [account, portfolioGrowthDateRange, otelContext]);

  const selectTransactionAccount = () => {
    const { accountId } = account;
    Store.dispatch(selectHistoricalTransactionIncludedAccounts(accountId));
    Store.dispatch(selectHistoricalTransactionTimePeriod('90'));
    history.push('transactions');
  };

  //priorBusinessDay is used incase the inception date is null
  useEffect(() => {
    otapi.context.with(otelContext, () => {
      getConversionHistory({
        beginDate: account.inceptionDate
          ? yyyymmdd(account.inceptionDate)
          : yyyymmdd(priorBusinessDay),
        endDate: yyyymmdd(portfolioAsOfDate),
        accounts: [getAccountDescriptor(account)]
      });
    });
  }, [account, portfolioAsOfDate, priorBusinessDay, otelContext]);

  const {
    productType,
    accountId,
    sweepAccountId,
    accountTypeId,
    shareClassConversion
  } = account;
  const isMFESweepAccount =
    account?.sweepAccount && account?.productType === MUTUAL_FUND;
  const mtsAccountBalance = isMFESweepAccount
    ? sweepAccountsMap[account.accountId]?.balanceDetails?.filter(
        (accountDetails) =>
          accountDetails?.balanceTypeValue?.balanceTypeName === 'Sweep_Accrual'
      )?.[0]?.amount || 0
    : null;

  const showShareClassTable =
    shareClassConversionTableEnabled() &&
    shareClassConversion &&
    conversionHistory.length > 0;
  const showPerformanceCharts =
    shareClassConversionEnabled() &&
    (isNil(shareClassConversion) ||
      (shareClassConversion && shareClassConversion.activeShareClassConverted));
  const { isMobile } = view;

  const isAccountDetails = true;

  return (
    <div className="holding-accounts-details-information">
      <PageHeader title="Account Details" />
      <AccountDetailsHeader
        account={account}
        numberOfAccounts={numberOfAccounts}
        owners={accountOwners}
        sweepAccountsMap={sweepAccountsMap}
      />

      {isDesktopLayout ? (
        <div className="account-details-tables">
          <AccountDetailsTable
            asOfDate={currentValueAsOfDate}
            header="Current Value"
            rows={isMFESweepAccount ? [...currentValue] : currentValue}
            isMFESweepAccount={isMFESweepAccount}
            mtsAccountData={{
              label: (
                <span>
                  <span className="mtd-accrual-account-details-info--title">
                    MTD Accrual{' '}
                  </span>
                  <ToolTip
                    className="cost-basis-tooltip-v2"
                    tooltipLabel={
                      <div className="cost-basis-tooltip-v2-box">
                        <div className="cost-basis-tooltip-v2-header">
                          MTD Accrual
                        </div>
                        <div className="cost-basis-tooltip-v2-content">
                          The total dollar value including the interest accrued
                          from the month beginning until today’s date
                        </div>
                      </div>
                    }>
                    <img
                      src={infoHelpIcon}
                      alt="info"
                      className="account-details-tooltip-v2-image account-details-table"
                    />
                  </ToolTip>
                </span>
              ),
              value: mtsAccountBalance
            }}
          />
          <AccountDetailsTable
            asOfDate={sharesAsOfDate}
            header={isCollegeSavings(productType) ? 'Units' : 'Shares'}
            rows={shares}
          />
          <AccountDetailsTransactions
            transactionUrls={transactionUrls}
            productType={productType}
            data={account}
            accountId={accountId}
            sweepAccountId={sweepAccountId}
            isMobile={isMobile}
            isAccountDetails={isAccountDetails}
            isRMDEligible={isRMDEligible}
            accountTypeId={accountTypeId}
          />
        </div>
      ) : (
        <div className="account-details-tables">
          <AccountDetailsTransactions
            transactionUrls={transactionUrls}
            productType={productType}
            data={account}
            accountId={accountId}
            sweepAccountId={sweepAccountId}
            isMobile={isMobile}
            isAccountDetails={isAccountDetails}
            isRMDEligible={isRMDEligible}
            accountTypeId={accountTypeId}
          />
          <AccountDetailsTable
            asOfDate={currentValueAsOfDate}
            header="Current Value"
            rows={currentValue}
            isMFESweepAccount={isMFESweepAccount}
            mtsAccountData={{
              label: (
                <span>
                  <span className="mtd-accrual-account-details-info--title">
                    MTD Accrual{' '}
                  </span>
                  <ToolTip
                    className="cost-basis-tooltip-v2"
                    tooltipLabel={
                      <div className="cost-basis-tooltip-v2-box">
                        <div className="cost-basis-tooltip-v2-header">
                          MTD Accrual
                        </div>
                        <div className="cost-basis-tooltip-v2-content">
                          The total dollar value including the interest accrued
                          from the month beginning until today’s date
                        </div>
                      </div>
                    }>
                    <img
                      src={infoHelpIcon}
                      alt="info"
                      className="account-details-tooltip-v2-image account-details-table"
                    />
                  </ToolTip>
                </span>
              ),
              value: mtsAccountBalance
            }}
          />
          <AccountDetailsTable
            asOfDate={sharesAsOfDate}
            header={isCollegeSavings(productType) ? 'Units' : 'Shares'}
            rows={shares}
          />
        </div>
      )}
      <div className="account-details-cost-basis">
        {isDesktopLayout ? (
          <AccountDetailsCostBasisTable
            asOfDate={asOfDate}
            unrealizedCostBasisRows={costBasisTableData}
            view={view}
            accountDetailsId={accountDetailsId}
          />
        ) : (
          <MobileAccountsCostBasisTable
            asOfDate={asOfDate}
            unrealizedCostBasisRows={costBasisTableData}
            view={view}
            accountDetailsId={accountDetailsId}
          />
        )}
      </div>
      {showPerformanceCharts && (
        <Fragment>
          <div className="account-details-graphs">
            <div className="account-details-activity-summary">
              <SectionHeaderWithSeparator
                title="Activity Summary"
                fromDate={activitySummaryDateRange.fromDate}
                toDate={activitySummaryDateRange.toDate}
                dateSeparator="-"
              />
              <div className="account-details-activity-summary-graphs">
                <PerformancePeriodFilter
                  filterType={ACTIVITY_SUMMARY_FILTER_TYPE}
                />
                {showNewErrorScreen ? (
                  <AccountDetailsWarningMessage
                    message={Content.activitySummaryWarningCallFailed}
                  />
                ) : (
                  <ActivitySummaryChart
                    summary={accountActivitySummary}
                    location={location}
                  />
                )}
                <div className="account-details-disclosure">
                  <span className="past-performance">
                    Past performance cannot guarantee future results.
                  </span>
                  <DisclosureModal
                    content={Content.activitySummaryDisclosure}
                    label="past-performance-disclosure-modal-account-activity-summary"
                  />
                </div>
              </div>
            </div>

            <div className="account-details-ror">
              <SectionHeaderWithSeparator
                title="Personal Rate of Return"
                fromDate={accountPerformanceDates.begin}
                toDate={accountPerformanceDates.end}
                dateSeparator="-"
              />
              <div className="account-details-ror-graphs">
                <ProductTypePerformanceChart
                  productPerformance={[accountPerformance]}
                  performancePeriods={performancePeriods}
                />
                <div className="account-details-disclosure">
                  <span className="past-performance">
                    Past performance cannot guarantee future results.
                  </span>
                  <DisclosureModal
                    content={Content.prrPerformanceDisclosure}
                    label="past-performance-disclosure-modal-prr"
                  />
                </div>
                All returns over one year are annualized.
              </div>
            </div>
          </div>

          <div className="account-details-portfolio-growth">
            <SectionHeaderWithSeparator
              title="Portfolio Growth"
              fromDate={portfolioGrowthDateRange.fromDate}
              toDate={portfolioGrowthDateRange.toDate}
              dateSeparator="-"
            />
            <div className="account-details-portfolio-growth-graph">
              <PerformancePeriodFilter
                filterType={PORTFOLIO_GROWTH_FILTER_TYPE}
              />
              {addPortfolioGrowthTableErrorScreenEnabled &&
              isPortfolioGrowthNull ? (
                <AccountDetailsWarningMessage
                  message={Content.portfolioGrowthChartWarningCallFailed}
                />
              ) : (
                <PortfolioGrowthChart growth={individualGrowth} />
              )}
              <div className="account-details-disclosure">
                {isCollegeSavings(productType) ? (
                  <span className="past-performance">
                    Past performance cannot guarantee future results. All
                    investment options are subject to market risk, including
                    possible loss of principal.
                  </span>
                ) : (
                  <span className="past-performance">
                    Past performance cannot guarantee future results.
                  </span>
                )}
                <DisclosureModal
                  content={Content.portfolioGrowthDisclosure}
                  label="past-performance-disclosure-modal-individual-growth"
                />
              </div>
              {!isCollegeSavings(productType) && (
                <div>
                  All Portfolios are subject to market risk, including possible
                  loss of principal.
                </div>
              )}
            </div>
          </div>
        </Fragment>
      )}
      {isDesktopLayout ? (
        <TransactionPendingTable
          rows={pendingTransactions}
          productType={productType}
          viewMoreLink={selectTransactionAccount}
          includePendingTransactions={true}
        />
      ) : (
        <MobileTransactionPendingTable
          groups={pendingTransactionsGroups}
          productType={productType}
          viewMoreLink={selectTransactionAccount}
          includePendingTransactions={true}
        />
      )}
      {showShareClassTable && (
        <ShareClassConversionTable
          portfolioAsOfDate={portfolioAsOfDate}
          conversionHistory={conversionHistory}
        />
      )}
      {isDesktopLayout ? (
        <TransactionHistoryTable
          includeTransactionHistory={true}
          rows={historyTransactions}
          productType={productType}
          viewMoreLink={selectTransactionAccount}
        />
      ) : (
        <MobileTransactionHistoryTable
          includeTransactionHistory={true}
          groups={historyTransactionsGroups}
          productType={productType}
          viewMoreLink={selectTransactionAccount}
        />
      )}

      <div className="info">
        {isMoneyMarketCutoffEnabled() ? (
          <p>
            All mutual fund pending transactions placed through the online
            Account Access system will be displayed as pending activity until
            the transactions are reflected in your Transaction History.
            Typically, transactions may be canceled until the market closes
            (usually 4 PM ET) on their trade date. For the Government Money and
            U.S. Treasury Money Funds, transactions may be canceled until the
            funds’ close (typically 5 PM ET) on their trade date. After these
            deadlines, transactions can no longer be canceled. Rollovers and
            Transfers will not show as a pending transaction until the funding
            is received from your prior institution.
          </p>
        ) : (
          <p>
            All mutual fund pending transactions placed through the online
            Account Access system will be displayed as pending activity until
            the transactions are reflected in your Transaction History.
            Transactions may be canceled until the market close (usually 4 p.m.
            ET) on their trade date. Once the market has closed on the trade
            date, the transaction can no longer be canceled. Rollovers and
            Transfers will not show as a pending transaction until the funding
            is received from your prior institution.
          </p>
        )}
        <p>Transaction history is updated through the previous business day.</p>
        {!isCollegeSavings(productType) && (
          <p>
            <span className="cost-basis-note">Cost Basis Note</span>: Noncovered
            shares will be redeemed first, regardless of your cost basis method,
            unless you request a redemption of specific tax lots. Your elected
            cost basis method applies only to covered shares. Covered mutual
            fund shares are those shares acquired on or after January 1, 2012,
            that are subject to cost basis reporting on Tax Form 1099-B. Mutual
            fund shares acquired prior to January 1, 2012, or without reliable
            cost basis information, are considered noncovered. For noncovered
            shares that you sell, we will calculate your cost basis using
            Average Cost with information we have, but we do not report
            noncovered cost basis to the IRS, and you do not have to use it when
            computing gains or losses on share redemptions.
          </p>
        )}
        {!isCollegeSavings(productType) && (
          <p>
            Selection of specific tax lots can only be accomplished in writing
            or by telephone. Please contact a T. Rowe Price representative for
            assistance.
          </p>
        )}
        {!isCollegeSavings(productType) && !isMutualFund(productType) && (
          <div className="banner">
            Looking to view or modify your Beneficiaries, Bank Information, or
            any other data related to this account? Please visit your{' '}
            <Link to="/profile">account profile</Link>.
          </div>
        )}
      </div>
    </div>
  );
};

AccountDetails.propTypes = {
  account: object.isRequired,
  accountActivitySummary: object.isRequired,
  accountOwners: string.isRequired,
  accountPerformance: object.isRequired,
  accountPerformanceDates: object.isRequired,
  costBasisTableData: object.isRequired,
  currentValue: array.isRequired,
  currentValueAsOfDate: string.isRequired,
  history: object.isRequired,
  historyTransactions: array.isRequired,
  historyTransactionsGroups: object.isRequired,
  individualGrowth: array.isRequired,
  isDesktopLayout: bool.isRequired,
  numberOfAccounts: number.isRequired,
  pendingTransactions: array.isRequired,
  pendingTransactionsGroups: object.isRequired,
  performancePeriods: array.isRequired,
  shares: array.isRequired,
  sharesAsOfDate: string.isRequired,
  transactionUrls: array.isRequired,
  activitySummaryDateRange: object,
  portfolioGrowthDateRange: object,
  isRMDEligible: bool.isRequired
};

const mapStateToProps = (state) => ({
  account: getAccountForDetails(state),
  accountActivitySummary: getActivitySummaryForAccount(state),
  accountOwners: getAccountOwners(state),
  accountPerformance: getPerformanceForAccount(state),
  accountPerformanceDates: getPerformanceDatesForAccount(state),
  asOfDate: getPortfolioAsOfDate(state),
  costBasisTableData: getAccountsCostBasisTableData(state),
  currentValue: getCurrentValueForAccount(state),
  currentValueAsOfDate: getCurrentValueAsOfDate(state),
  historyTransactions: getAccountHistoryTransactions(state),
  historyTransactionsGroups: getAccountHistoryTransactionsGroups(state),
  individualGrowth: state.individualGrowth,
  isDesktopLayout: isDesktopLayout(state),
  isRMDEligible: isRMDEligible(state),
  numberOfAccounts: getNumberOfAccountsForSelection(state),
  pendingTransactions: getAccountPendingTransactions(state),
  pendingTransactionsGroups: getAccountPendingTransactionsGroups(state),
  performancePeriods: state.performancePeriods,
  shares: getSharesForAccount(state),
  sharesAsOfDate: getSharesAsOfDate(state),
  transactionUrls: getAccountTransactionUrls(state),
  view: getView(state),
  accountDetailsId: getAccountDetailsId(state),
  activitySummaryDateRange: getFormattedDateRangeForActivitySummary(state),
  portfolioGrowthDateRange: getFormattedDateRangeForPortfolioGrowth(state),
  shouldRequestTaxes: shouldRequestTaxes(state),
  portfolioAsOfDate: getPortfolioAsOfDate(state),
  conversionHistory: getConversionHistoryResponse(state),
  isActivitySummaryNull: getActivitySummaryFilterIsNull(state),
  isPortfolioGrowthNull: getPortfolioGrowthFilterIsNull(state),
  priorBusinessDay: getPriorBusinessDay(state),
  sweepAccountsMap: getBrokerageAccountsFromSweepMap(state)
});

export default compose(
  withRouter,
  withOtelContext,
  connect(mapStateToProps)
)(AccountDetails);
